import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { predikatColor, useActiveTA } from "@/lib/academic";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Trophy, Medal, Award } from "lucide-react";
import { toast } from "sonner";

function RankBadge({ rank }) {
  if (rank === 1) return (
    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-sm">
      <Trophy className="h-4 w-4" />
    </div>
  );
  if (rank === 2) return (
    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-300 to-slate-500 text-white flex items-center justify-center">
      <Medal className="h-4 w-4" />
    </div>
  );
  if (rank === 3) return (
    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-300 to-orange-500 text-white flex items-center justify-center">
      <Award className="h-4 w-4" />
    </div>
  );
  return (
    <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-semibold text-sm tabular-nums">
      {rank}
    </div>
  );
}

export default function PeringkatKelasPage() {
  const { active: activeTA } = useActiveTA();
  const [tahunAjaran, setTahunAjaran] = useState("");
  const [semester, setSemester] = useState("ganjil");
  const [kelasId, setKelasId] = useState("");
  const [jenisUjianId, setJenisUjianId] = useState("all");

  const [taList, setTaList] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [jenisList, setJenisList] = useState([]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFilters = useCallback(async () => {
    try {
      const [ta, kel, ju] = await Promise.all([
        api.get("/tahun-ajaran"),
        api.get("/kelas", { params: { limit: 200 } }),
        api.get("/jenis-ujian"),
      ]);
      setTaList(ta.data.items || []);
      setKelasList(kel.data.items || []);
      setJenisList(ju.data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  useEffect(() => {
    if (activeTA && !tahunAjaran) {
      setTahunAjaran(activeTA.tahun_ajaran);
      setSemester(activeTA.semester);
    }
  }, [activeTA, tahunAjaran]);

  const load = useCallback(async () => {
    if (!kelasId || !tahunAjaran || !semester) { setItems([]); return; }
    setLoading(true);
    try {
      const params = { kelas_id: kelasId, tahun_ajaran: tahunAjaran, semester };
      if (jenisUjianId !== "all") params.jenis_ujian_id = jenisUjianId;
      const { data } = await api.get("/nilai/peringkat", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [kelasId, tahunAjaran, semester, jenisUjianId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="peringkat-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Nilai Ujian</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Peringkat Kelas</h1>
        <p className="text-slate-500 text-sm">Peringkat dihitung otomatis dari rata-rata seluruh nilai pada periode terpilih.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Tahun Ajaran *</Label>
            <Select value={tahunAjaran} onValueChange={setTahunAjaran}>
              <SelectTrigger className="mt-1 h-10" data-testid="peringkat-tahun"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>
                {[...new Set(taList.map((t) => t.tahun_ajaran))].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Semester *</Label>
            <Select value={semester} onValueChange={setSemester}>
              <SelectTrigger className="mt-1 h-10" data-testid="peringkat-semester"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ganjil">Ganjil</SelectItem>
                <SelectItem value="genap">Genap</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Kelas *</Label>
            <Select value={kelasId} onValueChange={setKelasId}>
              <SelectTrigger className="mt-1 h-10" data-testid="peringkat-kelas"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>
                {kelasList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Jenis Ujian (opsional)</Label>
            <Select value={jenisUjianId} onValueChange={setJenisUjianId}>
              <SelectTrigger className="mt-1 h-10" data-testid="peringkat-jenis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Jenis</SelectItem>
                {jenisList.map((j) => <SelectItem key={j.id} value={j.id}>{j.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold w-20">Peringkat</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nama Santri</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mapel</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Rata-rata</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Min / Max</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Predikat</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : !kelasId ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  Pilih kelas untuk menampilkan peringkat.
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  Belum ada nilai pada kombinasi ini.
                </TableCell></TableRow>
              ) : items.map((r) => (
                <TableRow key={r.santri_id} data-testid={`peringkat-row-${r.santri_id}`} className="hover:bg-emerald-50/30">
                  <TableCell><RankBadge rank={r.peringkat} /></TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-900">{r.nama}</div>
                    <div className="text-xs text-slate-500 font-mono">{r.nis}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700 tabular-nums">{r.jumlah_mapel} mapel</TableCell>
                  <TableCell className="font-heading text-xl font-bold text-emerald-700 tabular-nums">{r.rata_rata}</TableCell>
                  <TableCell className="text-sm text-slate-700 tabular-nums">{r.nilai_min} / {r.nilai_max}</TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${predikatColor(r.predikat)}`}>
                      {r.predikat}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
